import type { TemplateResult } from 'lit';
import { html } from 'lit';
import { EntityContent } from 'src/content/entity-content.js';
import '../../library/fields/index.js';
import '../../library/editors/index.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/editors/components/d-edit-classification.js';
import { customElement, property } from 'lit/decorators.js';
import { isEmptyOrInvalidString } from 'src/utilities/text';
import { t } from 'src/i18n';
import type { UpdateSectionItem } from '../update-section-item';
import type { ListDefinition } from '../list-definition';
import type { GuidelineViewEditItem } from './guideline-view-edit-item';
import type { GuidelineView } from './guideline-view';
import type { SectionField } from '../../library/components/section-field';

/**
 *
 */
@customElement('d-guideline-view')
export class DGuidelineView extends EntityContent<GuidelineView, GuidelineViewEditItem> {
  static readonly styles = EntityContent.styles;
  @property({ type: String })
  entityType = 'guidelines';
  @property({ type: Boolean })
  helpOpened = false;

  get helpOnly() {
    return (
      this.helpContent !== '' &&
      (!this.entityView.content || this.entityView.content === '<p><br></p>') &&
      this.mode === 'normal'
    );
  }

  renderViewMode() {
    return html` ${this.renderFieldSection('documents', this.viewFields(), this.entityView)} `;
  }

  async initializeEditItem() {
    this.editItem = {
      pages: this.entityView.pages,
      content: this.entityView.content,
      classification: this.entityView.classification,
      accessControl: this.entityView.accessControl,
      name: this.entityView.name,
    };
  }

  _setHelpOpened(newValue) {
    setTimeout(() => {
      if (newValue) {
        this.helpOpened = true;
      } else {
        this.helpOpened = false;
      }
    }, 0);
  }

  createNewItem() {
    return {
      content: '',
      template: '',
      category: '',
      status: '',
      name: '',
      accessControl: [],
      classification: 'NONE',
      pages: [this.pageId],
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  renderEditItem(item: GuidelineViewEditItem): TemplateResult<1> {
    return html`
      ${this.renderEditClassification(item)} ${this.renderFieldSection('guidelines', this.editFields(), item)}
    `;
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'classificationText',
        fieldForLabel: 'generalFields_access',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.classificationText !== '',
      },
      {
        field: 'content',
        type: 'view-html',
        hideLabel: true,
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return this.entityView;
  }

  private editFields(): SectionField[] {
    return [
      {
        field: 'name',
        type: 'edit-text',
        class: 'fullWidth',
        placeholder: t('reports.fieldMustBeFilledOut'),
        markIfEmpty: true,
        autofocus: !this.entityView.isConfirmedEntity,
        selectOnFocus: !this.entityView.isConfirmedEntity,
      },
      {
        field: 'content',
        type: 'edit-html',
        docsForLinking: this.entityView.docsForLinking,
      },
    ];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-guideline-view': DGuidelineView;
  }
}
